import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { addProgram, getProgram } from "../../utils/api";
import Modal from "react-modal";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { Link } from "react-router-dom";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow"; // Import PlayArrowIcon for the Start button
import Loader from "../Loader.js";
Modal.setAppElement("#root");

const Programs = ({ userId }) => {
    const [file, setFile] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [thumbnails, setThumbnails] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [scale, setScale] = useState(0.6);

    const onDrop = (acceptedFiles) => setFile(acceptedFiles[0]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: "application/pdf, image/*",
    });

    useEffect(() => {
        fetchUploadedFiles();
    }, [userId]);

    const fetchUploadedFiles = async () => {
        if (!userId) return;
        try {
            const files = await getProgram(userId);
            setUploadedFiles(files);
            const thumbnailPromises = files.map(async (file) => {
                const thumbnailUrl = await generateThumbnail(file.fileBlob);
                return { id: file._id, thumbnailUrl };
            });
            const thumbnailsData = await Promise.all(thumbnailPromises);
            setThumbnails(thumbnailsData);
        } catch (error) {
            console.error("Error fetching programs:", error);
        }
    };

    const handleUpload = async () => {
        if (!file) return alert("No file selected!");
        const formData = new FormData();
        formData.append("programFile", file);
        formData.append("userId", userId);
        try {
            await addProgram(formData);
            setFile(null);
            fetchUploadedFiles();
        } catch (error) {
            console.error("Error uploading program:", error);
            alert("Failed to upload program.");
        }
    };

    const getBlobFromFileBlob = (fileBlob) => {
        if (typeof fileBlob === "string") {
            const byteCharacters = atob(fileBlob);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
                const slice = byteCharacters.slice(offset, offset + 1024);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }
                byteArrays.push(new Uint8Array(byteNumbers));
            }
            return new Blob(byteArrays, { type: "application/pdf" });
        } else {
            return fileBlob;
        }
    };

    const generateThumbnail = async (fileBlob) => {
        const blob = getBlobFromFileBlob(fileBlob);
        const url = URL.createObjectURL(blob);
        try {
            const pdfDoc = await pdfjsLib.getDocument(url).promise;
            const page = await pdfDoc.getPage(1);
            const scale = 0.5;
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: context, viewport }).promise;
            return canvas.toDataURL();
        } finally {
            URL.revokeObjectURL(url);
        }
    };

    const openModal = (fileBlob) => {
        const blob = getBlobFromFileBlob(fileBlob);
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
    };

    const renderPDF = async (pdfDoc) => {
        const canvasContainer = document.getElementById("pdf-container");
        if (!canvasContainer) return;
        canvasContainer.innerHTML = "";
        for (let i = 1; i <= pdfDoc.numPages; i++) {
            const page = await pdfDoc.getPage(i);
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            const viewport = page.getViewport({ scale });
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: context, viewport }).promise;
            canvasContainer.appendChild(canvas);
        }
    };

    useEffect(() => {
        if (pdfUrl && isModalOpen) {
            pdfjsLib.getDocument(pdfUrl).promise
                .then((doc) => {
                    setPdfDoc(doc);
                    renderPDF(doc);
                })
                .catch((error) => {
                    console.error("Error loading PDF document:", error);
                });
        }
    }, [pdfUrl, isModalOpen]);

    useEffect(() => {
        if (pdfDoc) {
            renderPDF(pdfDoc);
        }
    }, [scale]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "70vh",
                backgroundColor: "#FFFAFB",
                padding: "10px",
            }}
        >
            {/* File Upload Area */}
            <div
                style={{
                    maxWidth: "400px",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                    backgroundColor: "#fff",
                    textAlign: "center",
                }}
            >
                <p style={{ fontFamily: "GveretLevin", fontSize: "24px", marginBottom: "20px" }}>
                    העלאת תוכנית אימונים
                </p>
                <div
                    {...getRootProps()}
                    style={{
                        border: "2px dashed #ccc",
                        padding: "20px",
                        borderRadius: "10px",
                        backgroundColor: "#FFFAFB",
                        cursor: "pointer",
                        marginBottom: "20px",
                    }}
                >
                    <input {...getInputProps()} />
                    <p style={{ color: file ? "#333" : "#888" }}>
                        {file
                            ? file.name.length > 10
                                ? `${file.name.slice(0, 7)}...${file.name.slice(file.name.lastIndexOf("."))}`
                                : file.name
                            : "PDF לחץ כאן לבחירת תוכנית"}
                    </p>
                </div>
                <button
                    onClick={handleUpload}
                    disabled={!file}
                    style={{
                        width: "100%",
                        padding: "10px",
                        backgroundColor: file ? "#28a745" : "#ccc",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: file ? "pointer" : "not-allowed",
                        fontSize: "16px",
                        fontFamily: "GveretLevin",
                    }}
                >
                    העלאה
                </button>
            </div>

            {/* Uploaded Files Grid */}
            <div style={{ maxWidth: "100%", marginTop: "20px", textAlign: "center" }}>
                <p style={{ fontFamily: "GveretLevin", fontSize: "20px", marginBottom: "10px" }}>
                    תוכניות
                </p>
                {uploadedFiles.length > 0 ? (
                    <div
                        style={{
                            width: "100%",
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                            gap: "20px",
                        }}
                    >
                        {uploadedFiles.map((file) => {
                            const thumbnail = thumbnails.find((t) => t.id === file._id);
                            return (
                                <div
                                    key={file._id}
                                    style={{
                                        position: "relative",
                                        textAlign: "center",
                                        padding: "10px",
                                        backgroundColor: "#fff",
                                        borderRadius: "5px",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => openModal(file.fileBlob)}
                                >
                                    {file.workoutProgramId && (
                                        <>
                                            <IconButton
                                                style={{ position: "absolute", top: 0, left: 0 }}
                                                component={Link}
                                                to={`/edit-program/${file.workoutProgramId}`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                style={{ position: "absolute", top: 0, right: 0 }}
                                                component={Link}
                                                to={`/start-workout/${file.workoutProgramId}`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <PlayArrowIcon />
                                            </IconButton>
                                        </>
                                    )}
                                    {thumbnail ? (
                                        <img
                                            src={thumbnail.thumbnailUrl}
                                            alt={file.fileName}
                                            style={{ width: "100%", height: "auto" }}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                width: "100%",
                                                height: "150px",
                                                backgroundColor: "#eee",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Loader />
                                        </div>
                                    )}
                                    <p
                                        style={{
                                            marginTop: "10px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {file.fileName}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p style={{ color: "#888" }}>לא הועלו תוכניות</p>
                )}
            </div>

            {/* Modal for Full PDF View */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                style={{
                    overlay: {
                        backgroundColor: "rgba(0, 0, 0, 0.75)",
                        zIndex: 1000,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    },
                    content: {
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        width: "80%",
                        height: "80%",
                        maxWidth: "1200px",
                        maxHeight: "90vh",
                        padding: "0",
                        margin: "0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "auto",
                    },
                }}
            >
                <button
                    onClick={closeModal}
                    style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        background: "none",
                        border: "none",
                        color: "#000",
                        fontSize: "30px",
                        cursor: "pointer",
                    }}
                >
                    ×
                </button>
                <div
                    style={{
                        position: "absolute",
                        top: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 1,
                        display: "flex",
                        gap: "10px",
                    }}
                >
                    <span
                        onClick={() => setScale((prev) => Math.min(prev + 0.1, 3))}
                        style={{
                            fontSize: "24px",
                            cursor: "pointer",
                            color: "#000",
                            userSelect: "none",
                        }}
                    >
                        +
                    </span>
                    <span
                        onClick={() => setScale((prev) => Math.max(prev - 0.1, 0.5))}
                        style={{
                            fontSize: "24px",
                            cursor: "pointer",
                            color: "#000",
                            userSelect: "none",
                        }}
                    >
                        -
                    </span>
                </div>
                <div
                    id="pdf-container"
                    style={{ width: "100%", height: "100%", overflow: "auto" }}
                ></div>
            </Modal>
        </div>
    );
};

export default Programs;